/*
 * 模块: 日期处理工具
 * 职责: 提供周期相关的日期计算和格式化功能
 * 依赖: dayjs
 * 导出: 日期处理函数
 * 限制: 文件<280行, 函数<30行, 纯函数无副作用
 */

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

/**
 * 从文件名中提取周期信息
 * 支持格式：
 * 1. W_2025_10_04 或 W_2025_10_04.xlsx
 * 2. US_热门搜索词_简单_Week_2025_09_06.csv
 * 3. Week_2025_09_06
 * 
 * @param filename 文件名
 * @returns 周期编号 (格式: YYYY-WNN) 或 null
 */
export const extractPeriodFromFilename = (filename: string): string | null => {
  // 尝试匹配 Week_YYYY_MM_DD 格式
  let match = filename.match(/Week_(\d{4})_(\d{2})_(\d{2})/i);
  
  // 如果没匹配到，尝试匹配 W_YYYY_MM_DD 格式
  if (!match) {
    match = filename.match(/W_(\d{4})_(\d{2})_(\d{2})/);
  }
  
  if (!match) return null;
  
  const [, year, month, day] = match;
  const date = dayjs(`${year}-${month}-${day}`);
  
  if (!date.isValid()) return null;
  
  // 计算ISO周数
  const weekNum = date.isoWeek();
  const weekYear = date.isoWeekYear();
  
  return `${weekYear}-W${weekNum.toString().padStart(2, '0')}`;
};

/**
 * 解析周期编号为年份和周数
 * 
 * @param weekNumber 周期编号 (格式: YYYY-WNN)
 * @returns { year, weekNum } 或 null
 */
export const parseWeekNumber = (weekNumber: string): { year: number; weekNum: number } | null => {
  const match = weekNumber.match(/^(\d{4})-W(\d+)/);
  if (!match) return null;
  
  return {
    year: parseInt(match[1]),
    weekNum: parseInt(match[2])
  };
};

/**
 * 计算指定年份的总周数（ISO周标准）
 * 
 * @param year 年份
 * @returns 总周数
 */
export const getTotalWeeksInYear = (year: number): number => {
  const lastDayOfYear = dayjs(`${year}-12-31`);
  return lastDayOfYear.isoWeek() === 1 ? 52 : lastDayOfYear.isoWeek();
};

/**
 * 获取指定周的日期范围
 * 
 * @param year 年份
 * @param weekNum 周数
 * @returns { startDate, endDate, saturdayDate }
 */
export const getWeekDateRange = (year: number, weekNum: number) => {
  const startDay = dayjs().year(year).isoWeek(weekNum).startOf('isoWeek');
  const endDay = startDay.endOf('isoWeek');
  // 使用周六作为显示标签（对应亚马逊格式 W_YYYY_MM_DD）
  const saturdayDay = endDay.subtract(1, 'day');
  
  return {
    startDate: startDay.format('MM-DD'),
    endDate: endDay.format('MM-DD'),
    saturdayDate: saturdayDay,
    weekDisplayLabel: `W_${saturdayDay.format('YYYY_MM_DD')}`
  };
};

/**
 * 从周期列表中提取所有年份
 * 
 * @param periods 周期列表
 * @returns 年份数组（已排序）
 */
export const extractYearsFromPeriods = (periods: Array<{ week_number: string }>): number[] => {
  const years: number[] = [];
  
  periods.forEach(period => {
    const match = period.week_number.match(/^(\d{4})-W/);
    if (match) {
      const year = parseInt(match[1]);
      if (!years.includes(year)) {
        years.push(year);
      }
    }
  });
  
  return years.sort((a, b) => a - b);
};

/**
 * 获取可用的年份范围（当前年份+1和有数据的年份）
 * 
 * @param periods 周期列表
 * @returns 年份数组（已排序）
 */
export const getAvailableYears = (periods: Array<{ week_number: string }>): number[] => {
  const currentYear = dayjs().year();
  const years = [currentYear, currentYear + 1];
  
  const dataYears = extractYearsFromPeriods(periods);
  dataYears.forEach(year => {
    if (!years.includes(year)) {
      years.push(year);
    }
  });
  
  return years.sort((a, b) => a - b);
};

